/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import * as React from "react";

import {
  BrowserRouter as Router,
  Switch,
  Route,
  useLocation,
} from "react-router-dom";
import { PLACEMENT, ToasterContainer } from "baseui/toast";

import { useApolloClient } from "@apollo/client";
import { SideNav } from "./Navigation/SideNav";
import { TopHeader } from "./Header/TopHeader";
import { AboutModal } from "./AboutModal";
import { SystemLoggerModal } from "./Shared/SystemLoggerModal";
import { ChangePasswordModal } from "./Shared/ChangePasswordModal";
import { PageNotFound } from "./Utils/PageNotFound";
import { RestrictedPage } from "./Utils/RestrictedPage";
import { FilesIndex } from "./File/FilesIndex";
import { ProjectAction } from "./ProjectState/ProjectActions";
import { ProjectReducer } from "./ProjectState/ProjectReducer";
import { ProjectState } from "./ProjectState/ProjectModels";
import { SiteContext, TOP_ITEM_ZINDEX } from "./Utils/SiteProps";
import { AxiosContext } from "./Utils/AuthContext";
import { useContext, useEffect, useReducer, useRef } from "react";
import { MappingsIndex } from "./Mapping/MappingsIndex";
import { Reference } from "./Reference/Reference";
import { QuickForm } from "./QuickForm/QuickForm";
import { Routes } from "./Utils/Routes";

export interface ProjectInfo {
  projectDispatch: React.Dispatch<ProjectAction>;
  projectState: ProjectState;
}

export const ProjectContext = React.createContext<ProjectInfo>({
  projectDispatch: (_value) => {},
  projectState: new ProjectState(),
});

/**
 * Renders SideNav for the normal app shell, but not when focus mode is on or
 * when this tab is the standalone Reference view (opened by the header's
 * "?" button in a new tab) — that view is meant to show only the top header
 * with the reference content beneath it, no navigation rail.
 */
const StandardSideNav = ({
  isFocusMode,
  projectInfo,
  onAboutClick,
  collapsed,
  onToggleCollapsed,
}: {
  isFocusMode: boolean;
  projectInfo: ProjectInfo;
  onAboutClick: () => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}): JSX.Element | null => {
  const location = useLocation();
  const isStandaloneReference = location.pathname === Routes.REFERENCE;

  if (isFocusMode || isStandaloneReference) {
    return null;
  }

  return (
    <SideNav
      projectInfo={projectInfo}
      onAboutClick={onAboutClick}
      collapsed={collapsed}
      onToggleCollapsed={onToggleCollapsed}
    />
  );
};

/**
 * Keeps QuickForm always mounted but hidden when not on the /quickform route,
 * so its in-memory state is preserved across tab navigation.
 */
const PersistentQuickForm = (): JSX.Element => {
  const location = useLocation();
  const isActive = location.pathname.startsWith("/quickform");

  return (
    <div style={{ display: isActive ? "block" : "none" }}>
      <QuickForm />
    </div>
  );
};

/**
 * The Dexter application that has a user context
 */
export const UserApp = (): JSX.Element => {
  // refs/components that shouldn't change
  const apolloClient = useApolloClient();
  const axiosContext = useContext(AxiosContext);
  const siteProps = useContext(SiteContext);

  // Setting nav visibility based on focus mode of transform engine
  const [isFocusMode, setIsFocusMode] = React.useState(false);

  const [isNavCollapsed, setIsNavCollapsed] = React.useState(false);
  const [isAboutOpen, setIsAboutOpen] = React.useState(false);
  const [isLoggerOpen, setIsLoggerOpen] = React.useState(false);
  const [isPasswordOpen, setIsPasswordOpen] = React.useState(false);

  // the ProjectReducer is a state machine that manages all state associated with the project
  const [projectState, projectDispatch] = useReducer(
    ProjectReducer,
    null,
    () => new ProjectState()
  );

  // Need this to access dispatch in end-to-end tests. Will not affect normal application behavior
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      (window as any).__projectDispatch__ = projectDispatch;
    }
  }, [projectDispatch]);

  // initialize project
  const isActiveRef = useRef(true);
  useEffect(() => {
    isActiveRef.current = true;
    projectDispatch([
      "navigate",
      { apolloClient, axiosContext, siteProps, isActiveRef },
      projectDispatch,
    ]);
    return () => {
      isActiveRef.current = false;
    };
  }, [apolloClient, axiosContext, siteProps]);

  // Modals previously owned by HeaderBar's user menu, now exist at app-level.
  return (
    <ProjectContext.Provider value={{ projectDispatch, projectState }}>
      <Router basename="/">
        <AboutModal
          isOpen={isAboutOpen}
          onClose={() => setIsAboutOpen(false)}
        />
        <SystemLoggerModal
          isOpen={isLoggerOpen}
          onClose={() => setIsLoggerOpen(false)}
        />
        <ChangePasswordModal
          isOpen={isPasswordOpen}
          onClose={() => setIsPasswordOpen(false)}
        />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100vh",
            width: "100vw",
          }}
        >
          {!isFocusMode && (
            <TopHeader
              onLoggerClick={() => setIsLoggerOpen(true)}
              onChangePasswordClick={() => setIsPasswordOpen(true)}
              projectInfo={{ projectState, projectDispatch }}
            />
          )}

          <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
            {!isFocusMode && (
              <StandardSideNav
                isFocusMode={isFocusMode}
                projectInfo={{ projectState, projectDispatch }}
                onAboutClick={() => setIsAboutOpen(true)}
                collapsed={isNavCollapsed}
                onToggleCollapsed={() => setIsNavCollapsed((c) => !c)}
              />
            )}

            <div
              style={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <ToasterContainer
                placement={PLACEMENT.topRight}
                overrides={{ Root: { style: { zIndex: TOP_ITEM_ZINDEX } } }}
              />
              {/*
                QuickForm is rendered here, outside of Switch, so it is never
                unmounted when navigating between routes.
              */}
              <PersistentQuickForm />

              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Switch>
                  <Route exact path={Routes.QUICKFORM}>
                    {null}
                  </Route>
                  <Route exact path={Routes.HOME}>
                    {null}
                  </Route>
                  <Route exact path={Routes.MAPPINGS}>
                    <MappingsIndex
                      isFocusMode={isFocusMode}
                      setIsFocusMode={setIsFocusMode}
                    />
                  </Route>
                  <Route exact path={Routes.JOBS}>
                    {null}
                  </Route>
                  <Route path={Routes.FILES}>
                    <FilesIndex />
                  </Route>
                  <Route exact path={Routes.RESTRICTED}>
                    <RestrictedPage />
                  </Route>
                  <Route exact path={Routes.REFERENCE}>
                    <Reference />
                  </Route>
                  <Route path="*">
                    <PageNotFound />
                  </Route>
                </Switch>
              </div>
            </div>
          </div>
        </div>
      </Router>
    </ProjectContext.Provider>
  );
};
